import Immutable from 'immutable';

import {getAt, setAt} from 'rlink-collections/immutable';
import formattedImgPath from 'rlink-repos/formattedImgPath';


import * as commands from './commands';
import Metadata from './meta';

import {randomString} from 'rlink-utils/string';

// note: all content is immutable and has a change history
export class Content {
  constructor (pathOrMeta, createCommands, parent=null, changes=[], withCreateCommand=true) {
    let meta = pathOrMeta;
    if (typeof pathOrMeta === 'string') {
      meta = new Metadata({path: pathOrMeta});
    }
    if (!meta || !meta.isMetadata) {
      throw new Error(`Invalid metadata object! Given: ${meta}`);
    }
    if (typeof meta.path !== 'string') {
      throw new Error("All content requires a path string!");
    }
    if (meta.registeredClassName && meta.registeredClassName !== this.constructor.registeredName) {
      throw new Error(`Content was instaniated with metadata for another class name! (own name is: ${this.constructor.registeredName}, name in metadata is: ${meta.registeredClassName})`);
    }
    this.meta = meta;

    this._parent = parent;

    if (withCreateCommand) {
      if (changes.length > 0) {
        throw new Error(`Newly created content cannot be instantiated with existing changes! (${this} has changes: ${changes.join(', ')})`)
      }
      changes = createCommands(this);
    }

    this._changes = changes;
  }

  static baseContentPath () {
    return '/content/';
  }

  get path () {
    return this.meta.path;
  }

  get checksum () {
    return this.meta.checksum || this._calculateChecksum();
  }

  get pathWithChecksum () {
    let makePathWithChecksum = (checksum) => {
      let match = this.path.match(/^(.+)(\.\w+)$/);
      return `${match[1]}-${checksum}${match[2]}`;
    }
    if (this.checksum instanceof Promise) {
      return this.checksum.then(makePathWithChecksum);
    } else {
      return makePathWithChecksum(this.checksum);
    }

  }


  _calculateChecksum () {
    let lastChange = this.lastChange;
    if (!lastChange) {
      throw new Error("Cannot calculate checksum without a command history!")
    }
    return lastChange.constructor.calculateChecksum(this.data);
  }

  get contentType () {
    if (!this.meta.contentType) {
      console.warn("should calculate CT based on ", this.path)
    }
    return this.meta.contentType || 'application/octet-stream';
  }

  get createdAt () {
    return this.meta.createdAt;
  }

  get lastModifiedAt () {
    return this.meta.lastModifiedAt;
  }

  async linkedContent (repo) {
    return [];
  }


  get lastChanges () {
    this._changes;
  }

  get lastChange () {
    let history = this.changeHistory();
    return history[history.length - 1];
  }

  * eachChange () {
    if (this._parent) {
      yield* this._parent.eachChange();
    }
    yield* this._changes;
  }

  changeHistory () {
    return Array.from(this.eachChange());
  }


  toString () {
    return `${this.constructor.name}("${this.path}")`;
  }

  inspect () {
    return this.toString();
  }

  static get registeredName () {
    if (! ('__registeredName' in this) )  {
      throw new Error(`Content class ${this.name} does not have a registered name! Are you importing it from your app's content regsitry module?`);
    }
    return this.__registeredName;
  }
  // only the registry function should ever call this
  static _setRegisteredName (name) {
    if (!name) {
      throw new Error(`Inavlid class name to register: ${name}`);
    }
    if ('__registeredName' in this && this.__registeredName !== name) {
      throw new Error(`Content class ${this.name} already has a registerd name "${this.__registeredName}", but is trying to re-register itself as "${name}"!`);
    }
    this.__registeredName = name;
  }

  static generatedPathBase() {
    return this.registeredName.toLowerCase();
  }

  static matchesContentPath (contentPath) {
    if (this.isAdministrative) {
      return (contentPath === this.path);
    }

    return (contentPath.indexOf(`${this.baseContentPath()}${this.generatedPathBase()}`) === 0);
  }

  get translationStrings() {
    return {};
  }


}

function defaultPathIterator (path) {
  return function *(content) {
    for (let [idx, obj] of getAt(content.data, path, [], () => []).entries()) {
      yield [path.concat([idx]), obj];
    }
  };
}

function defaultTranslationSource ({contentPath, translationAttr}) {
  return async function (objPath, obj, repo, _content) {
    let sourceContent = await repo.fetchContentAtPath(getAt(obj, contentPath));
    if (!sourceContent) {
      console.warn("failed to find translation source content at getAt(", obj, contentPath, ") -> ", getAt(obj, contentPath));
      return;
    }
    let messageAttrId = translationAttr;
    let sourceTranslationConfig = sourceContent.constructor.translatedAttributes[translationAttr];
    if (sourceTranslationConfig.keys) {
      messageAttrId = `${translationAttr}.${getAt(sourceContent.data, objPath.concat(sourceTranslationConfig.keys))}`;
    }
    return sourceContent.getMessageId(messageAttrId);
  };
}

export class StructuredDataContent extends Content {
  constructor (pathOrMeta, data, parent = null, changes = [], withCreateCommand = true, createCommandOverwrites = false) {
    // defer access to this to after super()
    let initData = (self) => Immutable.fromJS(
      data || self.constructor.defaultData())
    super(pathOrMeta, (self) => {
      return [commands.CreateNewContent.fromContent(self, {
              contentData: initData(self),
              overwrite: createCommandOverwrites,
            })]
    }, parent, changes, withCreateCommand);
    this._data = initData(this);
  }

  get isStructuredDataContent () {
    return this.constructor.isStructuredDataContent;
  }

  static get contentIcon () {
    return 'file-o';
  }

  static get isStructuredDataContent () {
    return true;
  }

  get data () {
    return this._data;
  }



  withContentCommandsApplied (commands, payload) {
    return new this.constructor(
      this.path,
      commands.reduce(
        (data, cmd) => cmd.applyToImmutable(data),
        this.data
      ),
      this,
      commands,
      false
    );
  }

  get linkedContentPaths () {
    if (typeof this.imageFormats === 'function') {
      let formattedImgPaths = new Set();
      for (let {src, formats, widths, heights} of instance.imageFormats()) {
        for (let {fileExt} of (formats|| [{contentType: "image/jpeg", fileExt: "jpg"}])) {
          for ( let width of (widths || []) ) {
            formattedImgPath.add(formattedImgPath(src, {width}, fileExt))
          }
          for (let height of (heights || [])) {
            formattedImgPath.add(formattedImgPath(src, {height}, fileExt))
          }
        }
      }
      return Array.from(formattedImgPaths);
    } else {
      return [];
    }
  }

  async linkedContent (repo) {
    let links = [];
    for (let path of this.linkedContentPaths ) {
      if (path && path.length) {
        let content = await repo.fetchContentAtPath(path);
        if (content) {
          links.push(content);
        } else {
          throw new Error(`Failed to find linked content for ${this} at ${path}`);
        }

      }

    }
    return links;
  }


  change (...pathAndValue) {
    let [path, replacementValue] = this._pathAndValueArgs(
      pathAndValue, 'change', 'replacementValue');


    return this.withContentCommandsApplied([
      commands.ChangeContentValue.fromContent(this, {
        valuePath: path,
        newValue: replacementValue
      })
    ]);
  }

  appendTo(...pathAndValue) {
    let [path, valueToAppend] = this._pathAndValueArgs(
      pathAndValue, 'appendTo', 'valueToAppend');

    return this.withContentCommandsApplied([
      commands.AppendContentValue.fromContent(this, {
        valuePath: path,
        newValue: valueToAppend
      })
    ]);
  }

  sortValues(...pathAndValue) {
    let [path, sortKeyPath] = this._pathAndValueArgs(
      pathAndValue, 'sortValues', 'sortKeyPath');

    return this.withContentCommandsApplied([
      commands.SortContentValues.fromContent(this, {
        valuePath: path,
        sortKeyPath: sortKeyPath
      })
    ]);
  }

  removeValue (...pathAndValue) {
    let [path] = this._pathAndValueArgs(
      pathAndValue, 'removeValue');

    return this.withContentCommandsApplied([
      commands.RemoveContentValue.fromContent(this, {
        valuePath: path
      })
    ]);

  }

  deleteCommand () {
    return commands.DeleteContent.fromContent(this);
  }

  _pathAndValueArgs (pathAndValue, methodName, ...requiredArgs) {
    let missingArgs = requiredArgs.slice(pathAndValue.length);

    if (missingArgs.length > 0) {
      throw new Error(`${this}.${methodName}() is missing required arguments ${missingArgs.join(', ')} (given: ${pathAndValue})`);
    }

    let path = pathAndValue.slice(
      0, requiredArgs.length > 0 ? -requiredArgs.length : undefined );
    let values = requiredArgs.length > 0 ? pathAndValue.slice(-requiredArgs.length) : [];
    return [path, ...values];
  }



  static async fetchOrMakeFromPath (repo, path, initData) {
    let content = await repo.fetchContentAtPath(path, this);
    if (!content) {
      content = new this(path, initData);
    }
    return content;
  }

  static instantiateFromData (pathOrMeta, data) {
    if ( typeof data !== "object" || !data) {
      throw new Error(`Content class ${this} must be instantiated with object data (given: ${data})`);
    }
    return new this(pathOrMeta, data, null, [], false);
  }

  static generateNewPath() {
    return `${this.baseContentPath()}${this.generatedPathBase()}/${randomString(16)}.json`;
  }

  static defaultData () {
    return {};
  }

  get translationStrings () {
    if (!this.constructor.translatedAttributes) {
      return super.translationStrings;
    }
    let strings = {};
    for (let [attrID, {path, values, keys, description, translationSource}] of Object.entries(this.constructor.translatedAttributes)) {
      if (translationSource) {
        // don't send for translation - we will re-use another content's translation
        continue;
      }
      if (values) {
        if (typeof path !== 'function') {
          path = defaultPathIterator(path);
        }
        for (let [_, obj] of path(this)) {
          let id = this.getMessageId(`${attrID}.${getAt(obj, keys)}`);
          let defaultMessage = getAt(obj, values, [], () => null);
          if (defaultMessage) {
            strings[id] = {
              id,
              description: typeof description === 'function' ? description(obj) : description,
              defaultMessage,
            };
          }
        }
      } else {
        let id = this.getMessageId(attrID);
        let defaultMessage = getAt(this.data, path, [], () => null);
        if (defaultMessage) {
          strings[id] = {
            id,
            description,
            defaultMessage,
          };
        }
      }
    }
    return strings;
  }

  getMessageId (attrID) {
    return `content.${this.constructor.registeredName}[${this.path}].${attrID}`;
  }

  async translateUsing (translations, repo) {
    if (!this.constructor.translatedAttributes) {
      throw new Error(`${this.constructor.name} is trying to be translated but has no translationAttributes set up!`)
    }
    let data = this.data;
    for (let [attrID, {path, values, keys, translationSource}] of Object.entries(this.constructor.translatedAttributes)) {


      if (translationSource && typeof translationSource !== 'function') {
        translationSource = defaultTranslationSource(translationSource);
      }

      if (values) {
        if (typeof path !== 'function') {
          path = defaultPathIterator(path);
        }
        for (let [idxPath, obj] of path(this)) {
          let id;
          if (translationSource) {
            id = await translationSource(idxPath, obj, repo, this);
          } else {
            id = this.getMessageId(`${attrID}.${getAt(obj, keys)}`);
          }
          console.log(this, `translation lookup for ${attrID} using id ${id}`);
          if (id && id in translations) {
            data = setAt(data, idxPath.concat(values), translations[id]);
            console.log('FOUND', translations[id]);
          } else {
            console.log('NOT FOUND :(');
            return null;
          }
        }
      } else {
        let id;
        if (translationSource) {
          id = await translationSource([], this.data, repo, this);
        } else {
          id = this.getMessageId(attrID);
        }
        console.log(this, `translation lookup for ${attrID} using id ${id}`);
        if (id && id in translations) {
          console.log('FOUND', translations[id]);
          data = setAt(data, path, translations[id]);
        } else {
          console.log('NOT FOUND :(');
          return null;
        }
      }
    }
    if (data === this.data) {
      return this;
    } else {
      let thisType = this.constructor;
      return new thisType(this.meta, data, null, [], true, true);
    }
  }

}



const IMAGE_TYPES = {
  jpg: 'jpg',
  jpeg: 'jpg',
  jpe: 'jpg',
  jif: 'jpg',
  jfi: 'jpg',
  jfif: 'jpg',
  jp2: 'jp2',
  j2k: 'jp2',
  jpf: 'jp2',
  jpx: 'jp2',
  jpm: 'jp2',
  mj2: 'jp2',
  jxr: 'jxr',
  hdp: 'jxr',
  wdp: 'jxr',
  webp: 'webp',
  gif: 'gif',
  png: 'png',
  apng: 'png',
  mng: 'mng',
  tiff: 'tiff',
  tif: 'tiff',
  svg: 'svg',
  svgz: 'svgz',
  xbm: 'xbm',
  bmp: 'bmp',
  dib: 'bmp',
  ico: 'ico',
}

export class BinaryContent extends Content {
  constructor (
      pathOrMeta,
      data,
      parent=null,
      changes=[],
      withCreateCommand=true,
      _commandClass=commands.CreateBinaryContent) {

    super(pathOrMeta, (self) => {
      return [_commandClass.fromContent(self, {
              binaryData: data
            })]
    }, parent, changes, withCreateCommand);
    this._data = data;
  }

  get data () {
    return this._data;
  }

  get blob () {
    return new Blob([this.data], {type: this.contentType });
  }

  get isBinaryContent () {
    return this.constructor.isBinaryContent;
  }
  static get isBinaryContent () {
    return true;
  }
  deleteCommand () {
    return commands.DeleteBinaryContent.fromContent(this);
  }
  static instantiateFromData (pathOrMeta, data) {
    if ( !(data instanceof Uint8Array || data instanceof ArrayBuffer) ) {
      throw new Error(`Binary content class ${this} must be instantiated with binary data (expected: Uint8Array or ArrayBuffer instance, given: ${data.toString()})`);
    }
    return new this(pathOrMeta, data, null, [], false);
  }

  static overwrite (pathOrMeta, data) {
    return new this(pathOrMeta, data, null, [], true, commands.SetBinaryContent);
  }


  static generateNewPath(fileType, subdir=null) {
    if (fileType in IMAGE_TYPES) {
      fileType = IMAGE_TYPES[fileType]; // normalized
      if (!subdir) {
        subdir = 'img';
      }
    } else {
      if (!subdir) {
        subdir = 'other'
      }
    }
    return `/content/${subdir}/${randomString(16)}.${fileType}`;
  }
}



// WEBPACK FOOTER //
// ./lib/rlink-repos/content.js